import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn, neutralIconTone } from '../../lib/utils'

/**
 * The DS2026 secondary/outline *surface* — neutral bordered fill + hover. It's
 * the shared skin under `Button appearance="outline"` (primary) and the
 * `Pagination` Prev/Next controls; each consumer layers its own focus-ring and
 * disabled treatment on top. Kept here as the single source so the two can't
 * drift apart. Must precede `buttonVariants`, which references it at module load.
 */
export const outlineSecondarySurface =
  'border border-fbui-secondary-border bg-fbui-secondary text-fbui-secondary-foreground hover:bg-fbui-secondary-hover hover:text-fbui-gray-900'

/**
 * Button — variant design adopted from the TailGrids component model (the system
 * our Figma is built on), wired to First Bite tokens and the Figma button specs:
 *
 *   variant     primary | danger | success | ghost | neutral   (intent / color)
 *   appearance  fill | outline                        (solid vs bordered)
 *   size        xs | sm | md | lg | xl              (height + padding + text)
 *   iconOnly    boolean                               (square, icon-sized button)
 *   leftIcon    ReactNode                             (icon before the text)
 *   rightIcon   ReactNode                             (icon after the text)
 *
 * Colors come straight from the Figma button page: fill intents are the -600
 * surface → -700 hover; the primary outline is a neutral gray button, while
 * danger/success outlines are tinted (-50 surface, -500 border, -200 hover).
 * Focus uses the translucent 4px intent glows (`--fbui-ring-*`). Disabled uses
 * Figma's explicit gray (gray-100 surface, gray-400 text), not opacity.
 *
 * Note: the Figma names the red intent "Error"; this keeps the established
 * `danger` API name (same red palette). There is no ghost in Figma — it's
 * retained here as a useful low-emphasis option. `ghost` hovers with the brand
 * accent (reads as a positive/primary low-emphasis action); `neutral` is the
 * same low-emphasis shape but hovers plain gray — for utility icon actions
 * (edit / copy / remove) that shouldn't read as brand-positive.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium tracking-[-0.2px] transition-colors outline-none focus-visible:ring-4 disabled:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      // Intent colors are applied via compoundVariants (fill vs outline differ).
      variant: { primary: '', danger: '', success: '', ghost: '', neutral: '' },
      appearance: { fill: '', outline: '' },
      size: {
        xs: 'text-xs [&_svg]:size-3.5',
        sm: 'text-xs [&_svg]:size-4',
        md: 'text-sm [&_svg]:size-5',
        lg: 'text-base [&_svg]:size-5',
        xl: 'text-base [&_svg]:size-6',
      },
      iconOnly: { true: '', false: '' },
    },
    compoundVariants: [
      // ── fill ────────────────────────────────────────────────────────────
      {
        variant: 'primary',
        appearance: 'fill',
        className:
          'bg-fbui-basil-600 text-fbui-base-white hover:bg-fbui-basil-700 focus-visible:ring-fbui-ring-brand disabled:bg-fbui-gray-100 disabled:text-fbui-text-disabled',
      },
      {
        variant: 'danger',
        appearance: 'fill',
        className:
          'bg-fbui-red-600 text-fbui-base-white hover:bg-fbui-red-700 focus-visible:ring-fbui-ring-error disabled:bg-fbui-gray-100 disabled:text-fbui-text-disabled',
      },
      {
        variant: 'success',
        appearance: 'fill',
        className:
          'bg-fbui-green-600 text-fbui-base-white hover:bg-fbui-green-700 focus-visible:ring-fbui-ring-success disabled:bg-fbui-gray-100 disabled:text-fbui-text-disabled',
      },
      // ── outline ─────────────────────────────────────────────────────────
      {
        variant: 'primary',
        appearance: 'outline',
        className: `${outlineSecondarySurface} focus-visible:ring-fbui-ring-default disabled:bg-fbui-secondary disabled:text-fbui-text-disabled disabled:border-fbui-gray-100`,
      },
      {
        variant: 'danger',
        appearance: 'outline',
        className:
          'border border-fbui-red-500 bg-fbui-red-50 text-fbui-red-700 hover:bg-fbui-red-200 focus-visible:border-fbui-red-600 focus-visible:ring-fbui-ring-error disabled:bg-fbui-background disabled:text-fbui-text-disabled disabled:border-fbui-gray-100',
      },
      {
        variant: 'success',
        appearance: 'outline',
        className:
          'border border-fbui-green-500 bg-fbui-green-50 text-fbui-green-700 hover:bg-fbui-green-200 focus-visible:border-fbui-green-600 focus-visible:ring-fbui-ring-success disabled:bg-fbui-background disabled:text-fbui-text-disabled disabled:border-fbui-gray-100',
      },
      // ── ghost (appearance-agnostic) ─────────────────────────────────────
      {
        variant: 'ghost',
        className:
          'text-fbui-foreground hover:bg-fbui-accent hover:text-fbui-accent-foreground focus-visible:ring-fbui-ring-default disabled:text-fbui-text-disabled',
      },
      // ── neutral (appearance-agnostic) ───────────────────────────────────
      // Low-emphasis like ghost, but a plain gray hover instead of the brand
      // accent — for utility icon actions (edit / copy / remove) that shouldn't
      // read as a positive/brand action. Reuses `neutralIconTone` (the shared
      // gray-500 → gray-100/gray-600 skin, also behind CloseButton/HelpPopover)
      // so the "neutral icon hover" can't drift across the system.
      {
        variant: 'neutral',
        className: `${neutralIconTone} focus-visible:ring-fbui-ring-default disabled:text-fbui-text-disabled`,
      },
      // ── sizing: text buttons (height + radius + horizontal padding) ──────
      {
        iconOnly: false,
        size: 'xs',
        className: 'h-6 gap-1.5 rounded-md px-4',
      },
      { iconOnly: false, size: 'sm', className: 'h-8 gap-1.5 rounded-md px-4' },
      { iconOnly: false, size: 'md', className: 'h-9 gap-1 rounded-lg px-5' },
      { iconOnly: false, size: 'lg', className: 'h-10 gap-1 rounded-lg px-6' },
      { iconOnly: false, size: 'xl', className: 'h-12 gap-1 rounded-lg px-6' },
      // ── sizing: icon-only buttons (square) ──────────────────────────────
      { iconOnly: true, size: 'xs', className: 'size-6 rounded-md' },
      { iconOnly: true, size: 'sm', className: 'size-8 rounded-md' },
      { iconOnly: true, size: 'md', className: 'size-9 rounded-lg' },
      { iconOnly: true, size: 'lg', className: 'size-10 rounded-lg' },
      { iconOnly: true, size: 'xl', className: 'size-12 rounded-lg' },
    ],
    defaultVariants: {
      variant: 'primary',
      appearance: 'fill',
      size: 'md',
      iconOnly: false,
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element as the button (Radix Slot), e.g. an anchor. */
  asChild?: boolean
  /** Icon rendered before the text (auto-sized per `size`). Ignored if iconOnly. */
  leftIcon?: React.ReactNode
  /** Icon rendered after the text (auto-sized per `size`). Ignored if iconOnly. */
  rightIcon?: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      appearance,
      size,
      iconOnly,
      leftIcon,
      rightIcon,
      asChild = false,
      type,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    // Default native buttons to type="button" so a Button outside a <form> never
    // triggers an accidental submit. When asChild is set we don't own the element.
    return (
      <Comp
        ref={ref}
        type={asChild ? type : (type ?? 'button')}
        className={cn(
          buttonVariants({ variant, appearance, size, iconOnly, className })
        )}
        {...props}
      >
        {/* asChild expects a single child element; don't inject icon siblings. */}
        {asChild ? (
          children
        ) : (
          <>
            {!iconOnly && leftIcon}
            {children}
            {!iconOnly && rightIcon}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'
